// src/components/Products/ProductList.js
import React, { useEffect, useState, useContext } from 'react';
import { getProductos, deleteProducto } from '../../services/api';
import { AuthContext } from '../../contexts/AuthContext';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Box,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

const ProductList = () => {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    const fetchProductos = async () => {
      try {
        const data = await getProductos();
        if (data.success) {
          setProductos(data.data);
        } else {
          setError('Error al obtener productos');
        }
      } catch (err) {
        setError(err.error || 'Error al obtener productos');
      } finally {
        setLoading(false);
      }
    };

    fetchProductos();
  }, []);

  const handleEdit = (id) => {
    navigate(`/products/edit/${id}`);
  };

  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setOpenDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const data = await deleteProducto(selectedId);
      if (data.success) {
        setProductos(productos.filter((prod) => prod.id !== selectedId));
      } else {
        setError('Error al eliminar producto');
      }
    } catch (err) {
      setError(err.error || 'Error al eliminar producto');
    } finally {
      setOpenDialog(false);
      setSelectedId(null);
    }
  };

  const handleDeleteCancel = () => {
    setOpenDialog(false);
    setSelectedId(null);
  };

  if (loading) return <CircularProgress />;

  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Container>
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={4} mb={2}>
        <Typography variant="h4">Productos</Typography>
        {user.permisos.crearProductos && (
          <Button variant="contained" color="primary" onClick={() => navigate('/products/new')}>
            Crear Producto
          </Button>
        )}
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Nombre</TableCell>
            <TableCell>Tipo de Producto</TableCell>
            <TableCell>Referencia Interna</TableCell>
            <TableCell>Código de Barras</TableCell>
            <TableCell>Precio de Venta</TableCell>
            <TableCell>Costo</TableCell>
            <TableCell>Peso</TableCell>
            <TableCell>Descripción</TableCell>
            <TableCell>Estado</TableCell>
            {(user.permisos.modificarPrecios || user.permisos.crearProductos) && <TableCell>Acciones</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {productos.map((producto) => (
            <TableRow key={producto.id}>
              <TableCell>{producto.id}</TableCell>
              <TableCell>{producto.name}</TableCell>
              <TableCell>{producto.productType}</TableCell>
              <TableCell>{producto.internalRef}</TableCell>
              <TableCell>{producto.barcode}</TableCell>
              <TableCell>{producto.salesPrice}</TableCell>
              <TableCell>{producto.cost}</TableCell>
              <TableCell>{producto.weight}</TableCell>
              <TableCell>{producto.description}</TableCell>
              <TableCell>{producto.estado}</TableCell>
              {(user.permisos.modificarPrecios || user.permisos.crearProductos) && (
                <TableCell>
                  {user.permisos.modificarPrecios && (
                    <Button variant="outlined" color="primary" onClick={() => handleEdit(producto.id)}>
                      Editar
                    </Button>
                  )}
                  {user.permisos.crearProductos && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => handleDeleteClick(producto.id)}
                      style={{ marginLeft: '8px' }}
                    >
                      Eliminar
                    </Button>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Diálogo de Confirmación para Eliminar */}
      <Dialog
        open={openDialog}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            ¿Estás seguro de que deseas eliminar este producto? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleDeleteConfirm} color="secondary" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProductList;
